import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { isPlatformHost } from "@/lib/host-resolver";

/**
 * Phase 23.4 — Global fallback toast.
 *
 * Mounted once at the app root. Whenever the URL on the platform host
 * carries `?fallback_reason=<reason>`, surface ONE friendly toast and
 * strip the param from the URL so it never re-fires on navigation.
 *
 * This complements the per-academy toast in `AcademySlugShell` which
 * only fires inside `/{slug}/*`. Cases like `domain_unknown` redirect
 * to dwrrah.com root (no slug shell), so we need a global handler too.
 *
 * Shown at most once per browser session per reason.
 */
export default function FallbackReasonToast() {
  const location = useLocation();
  const { t } = useI18n();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isPlatformHost()) return;
    const usp = new URLSearchParams(window.location.search);
    const reason = usp.get("fallback_reason");
    if (!reason) return;

    const sessionKey = `__dwrrah_fb_global_${reason}`;
    let already = false;
    try {
      already = window.sessionStorage.getItem(sessionKey) === "1";
    } catch {}

    if (!already) {
      try { window.sessionStorage.setItem(sessionKey, "1"); } catch {}
      // Try a reason-specific key first; fall back to the generic one.
      const specificKey = `customDomain.fallbackToast.${reason}`;
      const generic = t(
        "customDomain.fallbackToast",
        "تم فتح الأكاديمية عبر رابط منصة دورة الرئيسي لأن الدومين المخصص غير متاح حالياً.",
      );
      const message = t(specificKey, generic);
      try {
        toast.message(message);
      } catch {}
    }

    // Strip `fallback_reason` from the URL without reloading.
    try {
      usp.delete("fallback_reason");
      const qs = usp.toString();
      const newUrl =
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    } catch {}
  }, [location.pathname, location.search, t]);

  return null;
}